import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { GoogleLogo } from "@phosphor-icons/react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleSignIn() {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function formatDetail(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user) navigate("/dashboard"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(email, password); toast.success("Welcome back"); navigate("/dashboard"); }
    catch (err) { toast.error(formatDetail(err?.response?.data?.detail) || "Login failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 al-gradient text-white relative overflow-hidden">
        <div className="flex items-center gap-4 relative z-10">
          <div className="w-16 h-16 rounded-2xl bg-white/95 backdrop-blur flex items-center justify-center shadow-sm">
            <img src="/al-axe-mark.svg" alt="" aria-hidden="true" className="h-11 w-auto" />
          </div>
          <div>
            <div className="text-2xl font-semibold tracking-tight leading-none">AL-AXE</div>
            <div className="text-xs uppercase tracking-[0.2em] opacity-80 mt-1.5">Intelligent Operations</div>
          </div>
        </div>
        <div className="relative z-10">
          <h1 className="text-5xl font-semibold leading-tight tracking-tight mb-4">Your operations,<br/>captured with clarity.</h1>
          <p className="text-white/90 text-lg max-w-md">Conversations, plans, tasks, interviews, opportunities and lessons — one secure journal that thinks with you.</p>
        </div>
        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -left-32 -bottom-32 w-96 h-96 rounded-full bg-black/20 blur-3xl" />
        <div className="text-xs opacity-80 relative z-10">© AL-AXE {new Date().getFullYear()}</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="mb-8">
            <div className="lg:hidden mb-6 flex items-center gap-2">
              <img src="/al-axe-mark.svg" alt="" aria-hidden="true" className="h-9 w-auto" />
              <div className="font-semibold text-lg tracking-tight">AL-AXE</div>
            </div>
            <h2 className="text-3xl font-semibold tracking-tight">Sign in</h2>
            <p className="text-slate-500 text-sm mt-1">Access your operations journal.</p>
          </div>

          <button type="button" onClick={startGoogleSignIn} data-testid="google-signin" className="w-full inline-flex items-center justify-center gap-2 border border-slate-200 rounded-full py-3 font-medium text-slate-700 hover:bg-slate-50 mb-4">
            <GoogleLogo size={18} weight="bold"/> Continue with Google
          </button>
          <div className="flex items-center gap-3 my-4 text-xs uppercase tracking-widest text-slate-400">
            <div className="flex-1 h-px bg-slate-200"/> or <div className="flex-1 h-px bg-slate-200"/>
          </div>

          <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Email</label>
          <input data-testid="login-email" value={email} onChange={(e)=>setEmail(e.target.value)} required type="email" autoComplete="email" className="mt-1 mb-4 w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-teal-400" />

          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Password</label>
            <Link to="/forgot-password" data-testid="link-forgot" className="text-xs text-teal-600 hover:underline">Forgot?</Link>
          </div>
          <input data-testid="login-password" value={password} onChange={(e)=>setPassword(e.target.value)} required type="password" autoComplete="current-password" className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-teal-400" />

          <button data-testid="login-submit" disabled={busy} className="mt-6 w-full al-gradient text-white font-medium py-3 rounded-full disabled:opacity-60 active:scale-[0.98] transition-transform">
            {busy ? "Signing in…" : "Sign in →"}
          </button>

          <div className="mt-6 text-sm text-slate-500 text-center">
            New to AL-AXE? <Link to="/register" data-testid="link-register" className="text-slate-900 font-medium hover:underline">Create an account</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
